import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, ArrowRight, Play } from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import { useApp } from '../context/AppContext';
import * as ReactRouterDOM from 'react-router-dom';

const { useNavigate } = ReactRouterDOM as any;

const TypingEffect: React.FC<{ text: string }> = ({ text }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setDisplayedText(prev => prev + text[currentIndex]);
                setCurrentIndex(prev => prev + 1);
            }, 30); // 30ms per character for streaming effect
            return () => clearTimeout(timeout);
        }
    }, [currentIndex, text]);

    return <span>{displayedText}</span>;
};

export const AiOnboardingQuiz: React.FC = () => {
    const { getAllProducts } = useApp();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [answers, setAnswers] = useState<string[]>([]);
    
    // Default Questions
    const questions = [
        "Czego głównym przeznaczeniem będzie ten produkt?",
        "Jaki przedział cenowy bierzesz pod uwagę?",
        "Czy zależy Ci na konkretnych funkcjach lub cechach (np. bardzo cichy, mały, konkretny kolor)?"
    ];

    const [currentAnswer, setCurrentAnswer] = useState('');
    const [results, setResults] = useState<any>(null);

    const handleNext = () => {
        if (!currentAnswer.trim()) return;
        const newAnswers = [...answers, currentAnswer];
        setAnswers(newAnswers);
        setCurrentAnswer('');
        
        if (step < questions.length - 1) {
            setStep(step + 1);
        } else {
            generateRecommendations(newAnswers);
        }
    };

    const generateRecommendations = async (finalAnswers: string[]) => {
        setIsLoading(true);
        setStep(step + 1); // move to loading/results step
        try {
            const allProducts = getAllProducts().map((p: any) => ({
                id: p.id, title: p.title, price: p.price, desc: p.description
            }));

            const context = finalAnswers.map((a, i) => `Pytanie ${i+1}: ${questions[i]}\nOdpowiedź: ${a}`).join('\n\n');

            const prompt = `Jesteś ekspertem doradzającym w sklepie. Poniżej są odpowiedzi klienta na wywiad.
Twoim zadaniem jest dobrać maksymalnie 2 produkty z dostarczonej listy katalogowej, które najlepiej spełniają te kryteria.

Katalog sklepu: ${JSON.stringify(allProducts)}

Odpowiedzi klienta:
${context}

Zwróć odpowiedź w formacie JSON:
{
  "recommendations": [
     { "id": "id_produktu", "reason": "Krótkie uzasadnienie jednym zdaniem dlaczego to pasuje." }
  ],
  "message": "Krótkie podsumowanie z rekomendacją"
}
`;

            const payload = {
                model: 'gemini-3.1-flash-lite',
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                generationConfig: { responseMimeType: 'application/json' }
            };

            const responseStream = await fetch('api/generate_ai.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!responseStream.ok) {
                let errText = await responseStream.text();
                throw new Error(`Błąd API: ${responseStream.status} ${errText}`);
            }

            const responseData = await responseStream.json();
            const responseText = responseData.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
            
            const data = JSON.parse(responseText);
            const finalRecs = (data.recommendations || []).map((r: any) => ({
                product: getAllProducts().find((p:any) => p.id === r.id),
                reason: r.reason
            })).filter((r:any) => r.product != null);
            
            setResults({
                message: data.message || "Oto najlepsze propozycje dla Ciebie:",
                recs: finalRecs
            });

        } catch(e) {
            console.error("Quiz error", e);
            setResults({
                message: "Niestety wystąpił błąd analizy. Przejdź do ogólnego katalogu.",
                recs: []
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <button 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(true); }}
                className="flex items-center gap-2 bg-black text-white hover:bg-gray-800 transition-colors px-6 py-3 text-sm uppercase tracking-widest font-bold"
            >
                <Sparkles size={16} /> Pomóż mi wybrać
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-[200] bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-4">
            <button onClick={() => { setIsOpen(false); setStep(0); setAnswers([]); setResults(null); }} className="absolute top-8 right-8 text-black opacity-50 hover:opacity-100 uppercase text-xs tracking-widest font-bold">Zamknij</button>
            
            <div className="w-full max-w-2xl bg-white shadow-2xl border border-gray-100 p-12 relative animate-fade-in-up">
                
                {step < questions.length && (
                    <div className="space-y-8">
                        <div className="flex gap-2 mb-8">
                            {questions.map((_, i) => (
                                <div key={i} className={`h-1 flex-1 ${i <= step ? 'bg-black' : 'bg-gray-200'}`}></div>
                            ))}
                        </div>
                        <h2 className="text-3xl font-serif text-black leading-tight">
                            <TypingEffect text={questions[step]} />
                        </h2>
                        <div>
                            <input 
                                autoFocus
                                value={currentAnswer}
                                onChange={e => setCurrentAnswer(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleNext()}
                                placeholder="Wpisz odpowiedź..."
                                className="w-full border-b-2 border-gray-200 focus:border-black py-4 text-lg text-black placeholder-gray-500 bg-white outline-none transition-colors"
                            />
                        </div>
                        <button 
                            onClick={handleNext}
                            className="bg-black text-white px-8 py-4 font-bold uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-gray-800"
                        >
                            Dalej <ArrowRight size={16}/>
                        </button>
                    </div>
                )}

                {step === questions.length && isLoading && (
                    <div className="text-center py-20 flex flex-col items-center gap-6">
                        <Loader2 size={48} className="animate-spin text-black" />
                        <h2 className="text-2xl font-serif text-black">Analizuję Twoje odpowiedzi...</h2>
                        <p className="text-black">Sztuczna Inteligencja dobiera idealny produkt.</p>
                    </div>
                )}

                {results && !isLoading && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="text-center mb-12">
                            <Sparkles size={32} className="mx-auto text-black mb-4"/>
                            <h2 className="text-3xl font-serif text-black mb-4">Mamy to!</h2>
                            <p className="text-black text-lg"><TypingEffect text={results.message} /></p>
                        </div>

                        {results.recs.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
                                {results.recs.map((r: any, idx: number) => (
                                    <div key={idx} className="border border-gray-200 bg-white p-4 relative group cursor-pointer shadow-sm hover:shadow-md transition-shadow" onClick={() => { setIsOpen(false); navigate(`/product/${r.product.id}`); }}>
                                        <div className="w-full aspect-square bg-gray-50 mb-4 overflow-hidden">
                                           <img src={r.product.imageUrl} className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-700"/>
                                        </div>
                                        <h3 className="font-bold text-black line-clamp-1">{r.product.title}</h3>
                                        <p className="text-[10px] text-black uppercase tracking-widest my-2 font-bold">{r.product.price} PLN</p>
                                        <div className="bg-gray-50 p-3 text-xs text-black font-serif italic border border-gray-200 line-clamp-3">
                                            "<TypingEffect text={r.reason} />"
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-black mb-6">Niestety w tej chwili nie mamy nic idealnego, co spełniałoby wszystkie te kryteria.</p>
                                <button onClick={() => { setIsOpen(false); navigate('/kolekcja'); }} className="bg-black text-white px-6 py-3 font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-gray-800">Przeglądaj całą kolekcję</button>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};
